/**
 * Screen Reader Announcement Service
 * 
 * Provides a centralized service for announcing messages to screen readers
 * using ARIA live regions.
 */

class ScreenReaderService {
  constructor() {
    this.announcer = null;
    this.initialized = false;
  }

  /**
   * Initialize the screen reader announcer
   * Creates a hidden aria-live region in the DOM
   */
  init() {
    if (this.initialized) return;

    // Create announcer element
    this.announcer = document.createElement('div');
    this.announcer.id = 'screen-reader-announcer';
    this.announcer.setAttribute('role', 'status');
    this.announcer.setAttribute('aria-live', 'polite');
    this.announcer.setAttribute('aria-atomic', 'true');
    this.announcer.className = 'sr-only';

    // Apply styles to hide visually but keep accessible
    this.announcer.style.cssText = `
      position: absolute;
      left: -10000px;
      width: 1px;
      height: 1px;
      overflow: hidden;
    `;

    document.body.appendChild(this.announcer);
    this.initialized = true;
  }

  /**
   * Announce a message to screen readers
   * @param {string} message - Message to announce
   * @param {string} priority - 'polite' (default) or 'assertive'
   * @param {number} clearDelay - Time in ms before clearing message (default: 1000)
   */
  announce(message, priority = 'polite', clearDelay = 1000) {
    if (!this.initialized) {
      this.init();
    }

    if (!message || !this.announcer) return;

    // Set priority
    this.announcer.setAttribute('aria-live', priority);

    // Clear previous message
    this.announcer.textContent = '';

    // Small delay to ensure screen readers detect the change
    setTimeout(() => {
      this.announcer.textContent = message;
    }, 100);

    // Clear message after delay
    if (clearDelay > 0) {
      setTimeout(() => {
        this.announcer.textContent = '';
      }, clearDelay);
    }
  }

  /**
   * Announce a polite message (won't interrupt current announcements)
   * @param {string} message - Message to announce
   */
  polite(message) {
    this.announce(message, 'polite');
  }

  /**
   * Announce an assertive message (will interrupt current announcements)
   * @param {string} message - Message to announce
   */
  assertive(message) {
    this.announce(message, 'assertive');
  }

  /**
   * Announce form validation error
   * @param {string} fieldName - Name of the field with error
   * @param {string} errorMessage - Error message
   */
  announceError(fieldName, errorMessage) {
    this.assertive(`Error in ${fieldName}: ${errorMessage}`);
  }

  /**
   * Announce success message
   * @param {string} message - Success message
   */
  announceSuccess(message) {
    this.polite(`Success: ${message}`);
  }

  /**
   * Announce loading state
   * @param {string} message - Loading message
   */
  announceLoading(message = 'Loading...') {
    this.polite(message);
  }

  /**
   * Announce navigation/route change
   * @param {string} pageName - Name of the new page
   */
  announceNavigation(pageName) {
    this.polite(`Navigated to ${pageName}`);
  }

  /**
   * Announce search results
   * @param {number} count - Number of results
   * @param {string} query - Search query
   */
  announceSearchResults(count, query = '') {
    const message = query 
      ? `Found ${count} result${count !== 1 ? 's' : ''} for "${query}"`
      : `Found ${count} result${count !== 1 ? 's' : ''}`;
    this.polite(message);
  }

  /**
   * Clear current announcement
   */
  clear() {
    if (this.announcer) {
      this.announcer.textContent = '';
    }
  }

  /**
   * Destroy the announcer
   */
  destroy() {
    if (this.announcer && this.announcer.parentNode) {
      this.announcer.parentNode.removeChild(this.announcer);
    }
    this.announcer = null;
    this.initialized = false;
  }
}

// Create singleton instance
const screenReaderService = new ScreenReaderService();

// Vue plugin
const ScreenReaderPlugin = {
  install(Vue) {
    // Initialize service
    screenReaderService.init();

    // Add to Vue prototype for easy access in components
    Vue.prototype.$announcer = screenReaderService;
    Vue.prototype.$announce = (message, priority) => screenReaderService.announce(message, priority);
  }
};

// Export both the plugin and the service instance
export default ScreenReaderPlugin;
export { screenReaderService };
